import {SvgIcon} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import FlightIcon from "@mui/icons-material/Flight";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Calendar, Views, momentLocalizer} from "react-big-calendar";
import moment from "moment";
import {useTranslation} from "react-i18next";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./index.scss";

const localizer = momentLocalizer(moment);

function MatchEventItem({event}) {
	const home = event.homeTeamName || event.homeClub || "";
	const away = event.awayTeamName || event.awayClub || "";
	const hasResult =
		Number.isInteger(event.homeClubGoals) &&
		Number.isInteger(event.awayClubGoals);
	const result = hasResult
		? `${event.homeClubGoals}–${event.awayClubGoals}`
		: null;
	const ownHome = event.ownSide === "home";
	const competition =
		event.competitionSeason?.competition?.name || event.competitionName || "";

	return (
		<div className="cal-evt cal-evt--match">
			<div className="cal-evt__row">
				{event.ownSide && (
					<span
						className="cal-evt__badge cal-evt__badge--ha"
						title={ownHome ? "In casa" : "In trasferta"}
						aria-label={ownHome ? "In casa" : "In trasferta"}
					>
						{ownHome ? (
							<HomeIcon sx={{fontSize: 14}} />
						) : (
							<FlightIcon sx={{fontSize: 14}} />
						)}
					</span>
				)}
				<span className="cal-evt__label">
					{competition || "Competizione da inserire"}
				</span>
				{result && <span className="cal-evt__result">{result}</span>}
			</div>
			{event.startTimeToBeConfirmed && (
				<div className="cal-evt__sub" title="Orario da definire">
					OdD
				</div>
			)}
			{event.showScouting && (
				<div className="cal-evt__sub">
					{[
						event.scoutNames,
						event.scoutingTargetCount
							? `${event.scoutingTargetCount} obiettivi`
							: null,
						event.monitoredCount
							? `${event.monitoredCount} ${event.monitoredCount === 1 ? "monitorato" : "monitorati"}`
							: null,
					]
						.filter(Boolean)
						.join(" · ")}
				</div>
			)}
			{(home || away) && (
				<div className="cal-evt__sub">
					{home} Vs {away}
				</div>
			)}
		</div>
	);
}

function TrainingConeIcon() {
	return (
		<SvgIcon sx={{fontSize: 14}}>
			<path
				fillRule="evenodd"
				d="M10 2h4l5 17H5L10 2Zm-.6 6-.6 2h6.4l-.6-2H9.4Zm-1.5 5-.6 2h9.4l-.6-2H7.9ZM3 20h18v2H3z"
			/>
		</SvgIcon>
	);
}

function TrainingEventItem({event}) {
	const category = event.playersCategory || "";
	const roster = event.rosterName || "";
	const time = event.start ? moment(event.start).format("HH:mm") : "";
	const field = event.fieldName || "";

	return (
		<div className="cal-evt cal-evt--training">
			<div className="cal-evt__row">
				<span
					className="cal-evt__badge cal-evt__badge--training"
					title="Allenamento"
					aria-label="Allenamento"
				>
					<TrainingConeIcon />
				</span>
				<span className="cal-evt__label">{roster || category || "—"}</span>
				{time && <span className="cal-evt__time">{time}</span>}
			</div>
			{field && <div className="cal-evt__sub">{field}</div>}
		</div>
	);
}

function EventItem({event}) {
	if (event.__eventType === "match") return <MatchEventItem event={event} />;
	return <TrainingEventItem event={event} />;
}

export default function EventsCalendar(props) {
	const {
		events = [],
		view = Views.MONTH,
		onSelectEvent,
		onSelectSlot,
		onNavigate,
		onView,
		date,
		// Renderer alternativo della singola voce (es. partite osservate dallo scout).
		eventComponent,
	} = props;

	const {t} = useTranslation();
	const containerRef = useRef(null);
	const [calHeight, setCalHeight] = useState(600);

	useEffect(() => {
		const computeHeight = () => {
			if (!containerRef.current) return;
			const rect = containerRef.current.getBoundingClientRect();
			const h = Math.max(420, Math.floor(window.innerHeight - rect.top - 20));
			setCalHeight(h);
		};
		computeHeight();
		window.addEventListener("resize", computeHeight);
		window.addEventListener("orientationchange", computeHeight);
		return () => {
			window.removeEventListener("resize", computeHeight);
			window.removeEventListener("orientationchange", computeHeight);
		};
	}, []);

	const rbcEvents = useMemo(() => {
		return (events || []).map((e) => {
			const start = new Date(e.startDate);
			let end = e.endDate ? new Date(e.endDate) : new Date(e.startDate);

			const startDay = new Date(
				start.getFullYear(),
				start.getMonth(),
				start.getDate(),
			);
			const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

			if (endDay.getTime() !== startDay.getTime()) {
				end = new Date(startDay);
				end.setHours(23, 59, 59, 999);
			}
			if (end <= start) {
				end = new Date(start.getTime() + 60 * 60 * 1000);
			}

			return {
				...e,
				start,
				end,
				title:
					e.__eventType === "match"
						? e.playersCategory || e.opponentTeamName || "Partita"
						: e.rosterName || e.playersCategory || e.fieldName || "—",
				allDay: !!e.startTimeToBeConfirmed,
			};
		});
	}, [events]);

	const eventPropGetter = (event) => {
		const isMatch = event.__eventType === "match";
		let stripe = "var(--color-primary-alpha-40)";
		if (
			isMatch &&
			event.ownSide &&
			Number.isInteger(event.homeClubGoals) &&
			Number.isInteger(event.awayClubGoals)
		) {
			const goalsFor =
				event.ownSide === "home" ? event.homeClubGoals : event.awayClubGoals;
			const goalsAgainst =
				event.ownSide === "home" ? event.awayClubGoals : event.homeClubGoals;
			stripe =
				goalsFor > goalsAgainst
					? "var(--color-good)"
					: goalsFor === goalsAgainst
						? "var(--color-warning)"
						: "var(--color-critical)";
		}
		return {
			style: {
				// Gare in blu (striscia col risultato), allenamenti in verde
				backgroundColor: isMatch
					? "var(--color-primary-alpha-8)"
					: "var(--color-good-bg)",
				color: "var(--color-text)",
				borderRadius: "var(--radius-small)",
				border: "none",
				borderLeft: "3px solid " + (isMatch ? stripe : "var(--color-good)"),
				padding: "1px 4px",
				fontSize: "var(--font-size-caption)",
				cursor: "pointer",
			},
		};
	};

	const dayPropGetter = (date) => {
		const today = new Date();
		const isToday =
			date.getDate() === today.getDate() &&
			date.getMonth() === today.getMonth() &&
			date.getFullYear() === today.getFullYear();
		if (isToday) {
			return {
				style: {backgroundColor: "var(--color-primary-alpha-5)"},
			};
		}
		return {};
	};

	const minTime = useMemo(() => {
		const d = new Date();
		d.setHours(8, 0, 0, 0);
		return d;
	}, []);
	const maxTime = useMemo(() => {
		const d = new Date();
		d.setHours(23, 0, 0, 0);
		return d;
	}, []);

	return (
		<div className="events-calendar-container" ref={containerRef}>
			<Calendar
				style={{height: calHeight + "px", width: "100%"}}
				localizer={localizer}
				events={rbcEvents}
				startAccessor="start"
				endAccessor="end"
				view={view}
				onView={onView}
				views={[view === Views.WEEK ? Views.WEEK : Views.MONTH]}
				selectable={!!onSelectSlot}
				onSelectSlot={onSelectSlot}
				onSelectEvent={onSelectEvent}
				eventPropGetter={eventPropGetter}
				dayPropGetter={dayPropGetter}
				onNavigate={onNavigate}
				date={date}
				components={{event: eventComponent || EventItem}}
				min={minTime}
				max={maxTime}
				messages={{
					today: t("calendar_today", {defaultValue: "Oggi"}),
					previous: t("calendar_previous", {defaultValue: "‹"}),
					next: t("calendar_next", {defaultValue: "›"}),
					month: t("calendar_month", {defaultValue: "Mese"}),
					week: t("calendar_week", {defaultValue: "Settimana"}),
					day: t("calendar_day", {defaultValue: "Giorno"}),
					agenda: t("calendar_agenda", {defaultValue: "Agenda"}),
					showMore: (total) => `+${total}`,
				}}
				popup
				popupOffset={10}
			/>
		</div>
	);
}
